import { Button, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react'
import DatePicker, { Calendar, DateObject } from 'react-multi-date-picker';
import { CustomCheckBox } from '../ui/CustomCheckBox';
import { IOfficeContent } from '../../planEatApi/PlanEatFacade';
import { datesAreOnSameDay, prettyDate} from '../../config/dateFormatting';
import clsx from 'clsx';
import { Modal } from '../ui/Modal';
import { MdArrowDownward } from 'react-icons/md';
import { CancelCrudButtons } from '../ui/CancelCrudButtons';

const useStyles = makeStyles((theme: any) => ({
    defaultSeatingNumberContent:{
        display:'flex',
        justifyContent:'center',
        alignItems:'center',
        minHeight: 25,
        minWidth:25,
        maxWidth: 25,
        maxHeight: 25,
        
        borderRadius: '50%',
        border:'1px '+theme.palette.primary.main+' solid',
        color:theme.palette.primary.main,
        marginRight: 10,
    },
    generateStepTitle:{
        display:'flex',
        justifyContent:'start',
        alignItems:'center',
        fontSize: 16,
        fontWeight: 700,
        color: theme.palette.primary.main,
        marginTop: 15
    },
    overrideContainer:{
        display:"grid",
        gridTemplateColumns: "50% 50%",
        justifyContent: "space-evenly",
        alignItems: "center"
    },
    overrideColumn:{
        display:'flex',
        justifyContent:'start',
        alignItems:'center',
        flexDirection: "column"
    },
    overrideTitle:{
        fontSize: 16,
        fontWeight: 700,
        color: "#374251"
    },
    overrideSubtitle: {
        fontSize: 12,
        fontWeight: 400,
        color: "#374251",
        marginBottom: 10
    },
    selectDatesContainer:{
        display:"grid",
        gridTemplateColumns: "33% 66%",
        justifyContent: "space-evenly",
        alignItems: "center",
        marginTop: 20
    }, 
    explainerText:{
        fontSize: 14,
        fontWeight: 400,
        color: "#374251",
        marginTop: 10
    },
    summaryBox:{
        display:'flex',
        justifyContent:'start',
        alignItems:'center',
        flexDirection: "column",
        padding: 8,
        borderRadius: 8,
        minWidth: 250,
        height: 120,
        width: "fit-content",
    },
    summaryBoxContainer:{
        display:"flex",
        justifyContent: "space-evenly",
        alignItems: "center",
        width: "100%",
        marginTop: 25,
        columnGap: 10
    },
    summaryBoxTitle:{
        fontSize: 16,
        fontWeight: 700,
        color: "white"
    },
}));

interface Props {
    office: IOfficeContent
    minDate: Date;
    dateFrom: Date;
    setDateFrom: React.Dispatch<React.SetStateAction<Date>>;
    dateTo: Date;
    setDateTo: React.Dispatch<React.SetStateAction<Date>>;
    selectedDates: Date[],
    setSelectedDates: React.Dispatch<React.SetStateAction<Date[]>>;
    generateSeatingsModalHidden: boolean;
    setGenerateSeatingsModalHidden: React.Dispatch<React.SetStateAction<boolean>>;
    generateSeatings : (selectedDates: Date[], overrideAll: boolean) => void
}

export const GenerateModal: React.FC<Props> = ({office,minDate,dateFrom,setDateFrom,dateTo,setDateTo,selectedDates,setSelectedDates,generateSeatingsModalHidden,setGenerateSeatingsModalHidden,generateSeatings}) => {
    // Styling
    const classes = useStyles();

    // State
    const [overrideAll,setOverrideAll] = useState(true);

    const toDate = (dateObject: DateObject) : Date => {
        return dateObject.toDate()
    }

    const toDateObjects = (dates: Date[]) : DateObject[] => {
        var tmpDateObjects : DateObject[] = []
        dates.forEach((date) => {
            tmpDateObjects.push(new DateObject(date))
        })
        return tmpDateObjects
    }

    function handleDateFromChange(e: any) {
        if(e instanceof DateObject){
            setDateFrom(toDate(e));
            checkFromEarlierThanTo(toDate(e))
        }
    }

    function handleDateToChange(e: any) {
        if(e instanceof DateObject){
            setDateTo(toDate(e));
        }
    }

    function getSelectedDates(){
        var tmpSelectedDates : Date[]= []
        var tmpDateFrom = new Date(dateFrom)
        var tmpDateTo = new Date(dateTo)

        if(datesAreOnSameDay(tmpDateFrom,tmpDateTo)){
            if(office.defaultAvailability[tmpDateFrom.getDay()].available){
                tmpSelectedDates.push(tmpDateFrom)
            }
        }

        if(tmpDateFrom.getTime() < tmpDateTo.getTime()){
            while(!datesAreOnSameDay(tmpDateFrom,tmpDateTo)){
                var dayIndex = tmpDateFrom.getDay()
    
                if(office.defaultAvailability[dayIndex].available){
                    tmpSelectedDates.push(tmpDateFrom)
                }
    
                tmpDateFrom = new Date(tmpDateFrom)
                tmpDateFrom.setDate(tmpDateFrom.getDate() + 1)
            }

            if(datesAreOnSameDay(tmpDateFrom,tmpDateTo)){
                if(office.defaultAvailability[tmpDateFrom.getDay()].available){
                    tmpSelectedDates.push(tmpDateFrom)
                }
            }
        }
        setSelectedDates(tmpSelectedDates)
    }

    function checkFromEarlierThanTo(tmpDateFrom: Date){
        if(!datesAreOnSameDay(tmpDateFrom,dateTo)){
            if(tmpDateFrom.getTime() > dateTo.getTime()){
                setDateTo(new Date(tmpDateFrom))
            }
        }
    }

    function handleSelectedDatesChanges(e:any) {
        var tmpAvailableDates : Date[] = []

        if(e instanceof Array){
            e.forEach((dateObject) => {
                tmpAvailableDates.push((toDate(dateObject)));
            })
        }
        setSelectedDates(tmpAvailableDates)
    }

    useEffect(() => {
        getSelectedDates()
    }, [dateTo || dateFrom]);

    function postSeatings(){
        generateSeatings(selectedDates,overrideAll)
    }

    return (
        <Modal title="Generate Seatings" hidden={generateSeatingsModalHidden} setHidden={setGenerateSeatingsModalHidden}>
            <div>
                <div className={classes.generateStepTitle}>
                    <div className={classes.defaultSeatingNumberContent}>
                        1
                    </div>
                    Override options
                </div>
                <div className={classes.overrideContainer}>
                    <div className={classes.overrideColumn}>
                        <div className={classes.overrideTitle}>
                            Override all available dates?
                        </div>
                        <div className={classes.overrideSubtitle}>
                            Override every date in the calendar edited or not
                        </div>
                        <div onClick={() => {setOverrideAll(true)}}>
                            <CustomCheckBox selected={overrideAll}/>
                        </div>
                    </div>
                    <div className={classes.overrideColumn}>
                        <div className={classes.overrideTitle}>
                            Override only un-edited dates?
                        </div>
                        <div className={classes.overrideSubtitle}>
                            Override ovly the dates in the calendar that have not been indiviudally edited.
                        </div>
                        <div onClick={() => {setOverrideAll(false)}}>
                            <CustomCheckBox selected={!overrideAll}/>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <div className={classes.generateStepTitle}>
                    <div className={classes.defaultSeatingNumberContent}>
                        2
                    </div>
                    Date range
                </div>
                <div className={classes.explainerText}>
                    The first selectable date, represents the first date where no booking the created
                </div>
                <div className={classes.selectDatesContainer}>
                    <div className={classes.overrideColumn}>
                        <div className={clsx(classes.overrideColumn,classes.overrideTitle)}>
                            Date from
                        </div>
                        <DatePicker      
                            containerStyle={{background:"white !important"}}  
                            minDate={minDate}
                            multiple={false}
                            value={dateFrom} 
                            onChange={handleDateFromChange}
                            weekStartDayIndex={1}
                            showOtherDays={true}
                            mapDays={({ date }) => {
                                let isWeekend = [0, 6].includes(date.weekDay.index)
                                
                                if (isWeekend) return {
                                disabled: true,
                                style: { color: "#ccc" },
                                onClick: () => alert("weekends are disabled")
                                }
                            }}
                        />
                        <div style={{marginBottom:10,marginTop:10}}>
                            <MdArrowDownward/>
                        </div>
                        <div className={clsx(classes.overrideColumn,classes.overrideTitle)}>
                            Date To
                        </div>
                        <DatePicker 
                            multiple={false}
                            minDate={minDate}
                            value={dateTo} 
                            onChange={handleDateToChange}
                            weekStartDayIndex={1}
                            showOtherDays={true}
                            mapDays={({ date }) => {
                                let isWeekend = [0, 6].includes(date.weekDay.index)
                                
                                if (isWeekend) return {
                                disabled: true,
                                style: { color: "#ccc" },
                                onClick: () => alert("weekends are disabled")
                                }
                            }}
                        />
                    </div>
                    <div className={classes.overrideColumn}>
                        <Calendar 
                            numberOfMonths={2}
                            minDate={minDate}
                            multiple={true}
                            value={toDateObjects(selectedDates)} 
                            onChange={handleSelectedDatesChanges}
                            weekStartDayIndex={1}
                            mapDays={({ date }) => {
                                let isWeekend = [0, 6].includes(date.weekDay.index)
                                
                                if (isWeekend) return {
                                disabled: true,
                                style: { color: "#ccc" },
                                onClick: () => alert("weekends are disabled")
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div>
                <div className={classes.generateStepTitle}>
                    <div className={classes.defaultSeatingNumberContent}>
                        3
                    </div>
                    Generation summary
                </div>
                <div className={classes.summaryBoxContainer}>
                    <div className={classes.summaryBox} style={{background:"#6178ed"}}>
                        <div className={classes.summaryBoxTitle}>
                            Date range
                        </div>
                        <div className={classes.overrideColumn} style={{color:"#FFFFFF"}}>
                                {prettyDate(dateFrom,false,true)}
                                <MdArrowDownward/>
                                {prettyDate(dateTo,false,true)}
                        </div>
                    </div>
                    <div className={classes.summaryBox} style={{background:"#1E1151"}}>
                        <div className={classes.summaryBoxTitle}>
                            No. of days
                        </div>
                        <div style={{color:"white",fontSize:24,fontWeight:700,marginTop:10}}>
                            {selectedDates.length}
                        </div>
                    </div>
                    <div className={classes.summaryBox} style={{background:"#9354F9"}}>
                        <div className={classes.summaryBoxTitle}>
                            No. of seatings
                        </div>
                        <div style={{color:"white",fontSize:24,fontWeight:700,marginTop:10}}>
                            {selectedDates.length * office.defaultSeatings.length}
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <CancelCrudButtons 
                    hidden={generateSeatingsModalHidden} 
                    setHidden={setGenerateSeatingsModalHidden} 
                    postButtonText={"Generate " + selectedDates.length * office.defaultSeatings.length + " seating(s)"}
                    isDelete={false} 
                    postEntity={postSeatings} />
            </div>
        </Modal>
    );
}